import ErrorIllustration from 'assets/svg/error/error-illustration.svg'
import OvalIcon from 'assets/svg/oval/oval.svg'
import DateIcon from 'assets/svg/transaction/calendar.svg'
import CancelOrderIcon from 'assets/svg/transaction/cancel.svg'
import ConfirmedOrderIcon from 'assets/svg/transaction/confirm-order.svg'
import EmptyNotifIllustration from 'assets/svg/transaction/illustration-empty-transaction.svg'
import DeliveryOrderIcon from 'assets/svg/transaction/ondelivery.svg'
import ProcessOrderIcon from 'assets/svg/transaction/onprocess.svg'
import PaymentPendingIcon from 'assets/svg/transaction/payment-pending.svg'
import PaymentSuccessIcon from 'assets/svg/transaction/payment-success.svg'
import Grid from 'common/components/Grid'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import NotificationSkeleton from 'services/Notification/widgets/Notification/Skeleton'
import styled from 'styled-components'

interface ITransactionNotification {
  items: any[]
  isLogin: boolean
  isLoading: boolean
  isError: boolean
  onContentClick?: Function
  retryGetData?: Function
  onClickReadAll?: Function
}

const ContainerTransaction = styled.div`
  width: 300px;
  max-height: 278px;
  overflow-y: auto;
  margin: auto;
  padding: 10px;
`
const WrapperTransactionOffline = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px;
`
const WrapperTransactionOnline = styled.a`
  display: flex;
  cursor: pointer;
`
const ContentTransactionOnline = styled.div`
  display: flex;
  flex-direction: column;
  width: 230px;
  margin-left: 13px;
  margin-right: 10px;
  margin-bottom: 5px;
  cursor: pointer;
`
const DateWrapper = styled.div`
  display: flex;
`
const IconOnline = styled.div`
  position: relative;
  top: 2px;
  width: 30px;
  margin-left: 5px;
`
const IconOffline = styled.div`
  margin: 0 auto;
`
const Icon = styled.span`
  position: relative;
  left: 5px;
  bottom: 8px;
`

const StyledDate = styled.div`
  position: relative;
  bottom: 4px;
  margin-right: 4px;
`

const StyledGrid = styled(Grid)`
  border-top: 1px solid ${theme.colors.whiteTwo};
`

const ReadAllWrapper = styled.div`
  padding: 8px 0 8px 12px;
  text-align: left;
  text-decoration: none;
`

const ReadAll = styled.span`
  color: ${theme.colors.orange};
  ${theme.typography.bparagraph}
  cursor: ${(props) => (props.isLoading ? 'none' : 'pointer')};
  opacity: ${(props) => (props.isLoading ? 0.5 : 1)};
`

const ShowAllWrapper = styled.div`
  padding: 8px 12px 8px 0;
  text-align: right;
  text-decoration: none;
`

const ShowAll = styled.a`
  color: ${theme.colors.orange};
  ${theme.typography.bparagraph}
`

const WrapperEmptyOrErrorNotif = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px;
`

const ImageEmptyOrErrorNotif = styled.div`
  margin: 0 auto;
`

const WrapperText = styled.div`
  width: 100%;
  text-align: -webkit-center;
`

const StyledText = styled.div`
  opacity: ${(props) => (props.isError ? 1 : 0.5)};
  margin-bottom: ${(props) => props.marginBottom};
  margin-top: ${(props) => props.marginTop};
  width: ${(props) => props.width};
`

const TryAgain = styled.span`
  color: ${theme.colors.orange};
  cursor: pointer;
`

const ShowAllLink = styled.a`
  ${theme.typography.bparagraph}
  color: ${theme.colors.orange};
  border-top: 1px solid ${theme.colors.whiteTwo};
  padding-top: 10px;
  padding-bottom: 10px;
  display: block;
  width: 100%;
  text-decoration: none;
  text-align: center;
`

const getIcon = (status: string): JSX.Element => {
  switch (status?.toLowerCase()) {
    case 'order rejected': {
      return <CancelOrderIcon />
    }
    case 'waiting for payment': {
      return <PaymentPendingIcon />
    }
    case 'payment succeed': {
      return <PaymentSuccessIcon />
    }
    // status flag for production only
    case 'payment verified': {
      return <PaymentSuccessIcon />
    }
    case 'order process': {
      return <ProcessOrderIcon />
    }
    // status flag for production only
    case 'order processed': {
      return <ProcessOrderIcon />
    }
    case 'order in shipment': {
      return <DeliveryOrderIcon />
    }
    case 'order received': {
      return <ConfirmedOrderIcon />
    }
    default:
      return null
  }
}

function Transaction({
  items,
  isLogin,
  isLoading,
  isError,
  onContentClick = () => null,
  retryGetData = () => null,
  onClickReadAll = () => null,
}: ITransactionNotification): JSX.Element {
  const offlineTransaction = (
    <WrapperTransactionOffline data-testid="transaction-offline">
      <IconOffline>
        <EmptyNotifIllustration />
      </IconOffline>
      <StyledText marginBottom="79px">
        <Typography textAlign="center" type="rparagraph">
          Anda harus masuk terlebih dahulu
        </Typography>
      </StyledText>
    </WrapperTransactionOffline>
  )

  const onlineTransaction = items?.slice(0, 20).map((item, index) => {
    return (
      <WrapperTransactionOnline
        key={item.notif_id}
        data-testid={`item-${index}`}
        href={`${WEB_DESKTOP_RALALI_URL}/customer/transaction-details?order_serial=${item.order_serial.replace(
          new RegExp('/', 'g'),
          '---',
        )}`}
        target="_blank"
        onClick={() => onContentClick(item.is_read, item.notif_id)}
      >
        <IconOnline>{getIcon(item.notif_title_en)}</IconOnline>
        <ContentTransactionOnline>
          <Typography
            type="rlabel"
            fontWeight={item.is_read ? 'normal' : 'bold'}
            marginBottom="2px"
          >
            {item.notif_title_id}
          </Typography>
          <Typography
            fontSize="9px"
            lineHeight="1.22"
            marginBottom="3px"
          >
            <StyledText>{`#${item.order_serial}`}</StyledText>
          </Typography>
          <Typography type="rcaption" lineHeight="1.2" maxLine={2}>
            {item.notif_desc_id}
          </Typography>
          <StyledText>
            <DateWrapper>
              <StyledDate>
                <DateIcon />
              </StyledDate>
              <Typography fontSize="8px">
                {item.notification_date}
              </Typography>
            </DateWrapper>
          </StyledText>
        </ContentTransactionOnline>
        {!item.is_read ? (
          <Icon>
            <OvalIcon />
          </Icon>
        ) : null}
      </WrapperTransactionOnline>
    )
  })

  const errorNotifData = (
    <WrapperEmptyOrErrorNotif>
      <ImageEmptyOrErrorNotif>
        <ErrorIllustration />
      </ImageEmptyOrErrorNotif>
      <WrapperText>
        <StyledText
          marginBottom="34px"
          marginTop="30px"
          width="230px"
          isError={isError}
        >
          <Typography textAlign="center" type="rparagraph">
            <>
              Mohon maaf terjadi kesalahan. Silahkan coba beberapa
              saat lagi atau&nbsp;
              <TryAgain
                data-testid="retry-get-data-transaction"
                onClick={() => {
                  retryGetData()
                }}
              >
                coba lagi
              </TryAgain>
            </>
          </Typography>
        </StyledText>
      </WrapperText>
    </WrapperEmptyOrErrorNotif>
  )

  const emptyNotifData = (
    <WrapperEmptyOrErrorNotif data-testid="null-notifs-data">
      <ImageEmptyOrErrorNotif>
        <EmptyNotifIllustration width="140px" height="140px" />
      </ImageEmptyOrErrorNotif>
      <StyledText marginBottom="24px">
        <Typography textAlign="center" type="rparagraph">
          Anda belum memiliki transaksi
        </Typography>
      </StyledText>
    </WrapperEmptyOrErrorNotif>
  )

  const renderOnlineTransaction = () => {
    if (isLoading) {
      return <NotificationSkeleton />
    }
    if (isError) {
      return errorNotifData
    }
    if (!items.length) {
      return emptyNotifData
    }
    if (items.length) return onlineTransaction
    return null
  }

  return (
    <>
      <ContainerTransaction data-testid="transaction-container">
        {!isLogin && offlineTransaction}
        {isLogin && renderOnlineTransaction()}
      </ContainerTransaction>
      {(() => {
        if (isLogin && !isError && items.length) {
          return (
            <StyledGrid templateColumn={2}>
              <>
                <ReadAllWrapper>
                  <ReadAll
                    isLoading={isLoading}
                    onClick={() =>
                      !isLoading ? onClickReadAll() : null
                    }
                  >
                    Tandai semua dibaca
                  </ReadAll>
                </ReadAllWrapper>
                <ShowAllWrapper>
                  <ShowAll
                    href={`${WEB_DESKTOP_RALALI_URL}/customer/notifikasi`}
                    target="_blank"
                  >
                    Lihat Selengkapnya
                  </ShowAll>
                </ShowAllWrapper>
              </>
            </StyledGrid>
          )
        }
        if ((isLogin && isError) || (isLogin && !items.length)) {
          return (
            <ShowAllLink
              href={`${WEB_DESKTOP_RALALI_URL}/customer/notifikasi`}
              target="_blank"
              data-testid="notif-link"
            >
              Lihat Selengkapnya
            </ShowAllLink>
          )
        }
        return null
      })()}
    </>
  )
}

export default Transaction
